import { Injectable } from '@angular/core';
import { TableLazyLoadEvent } from 'primeng/table';
import { BehaviorSubject, Observable, catchError, take } from 'rxjs';
import { ServiceBase } from './servicebase.service';

@Injectable({
  providedIn: 'root'
})
export class AdminService extends ServiceBase {
  loadingUsersList: boolean = false;
  usersList$: BehaviorSubject<ServerModels.UserInfoViewModel[]> = new BehaviorSubject<ServerModels.UserInfoViewModel[]>([]);
  userListTotalRecords$: BehaviorSubject<number> = new BehaviorSubject<number>(0);


  public addOrUpdateSupervisor(supervisors: ServerModels.SupervisorsView[]): Observable<number> {
    return this.http.post<ServerModels.SupervisorsView>(`${this.apiRoot}/api/Admin/AddOrUpdateSupervisor`, supervisors, this.httpOptions).pipe(catchError(this.throwError<any>('addOrUpdateSupervisor', null, null, null)));
  }

  public approveDenyChangeRequest(changeReq: ServerModels.ENESS_XREF_UserProfileChangeRequests): Observable<ServerModels.ENESS_ApplicationUser[]> {
    return this.http.post(`${this.apiRoot}/api/Account/ApprovalOrDeniedChangeRequest`, changeReq, this.httpOptions).pipe(catchError(this.throwError<any>('approveDenyChangeRequest', 'defaultMsg', 'Approve/Deny failed:', null )));
  }

  public approveDenyUser(userInfo: any): Observable<ServerModels.ENESS_ApplicationUser[]> {
    return this.http.post(`${this.apiRoot}/api/Admin/ApproveOrDenyUser`, userInfo, this.httpOptions).pipe(catchError(this.throwError<any>('approveDenyUser', 'defaultMsg', 'Approve/Deny failed:', null )));
  }

  public disableEnableUser(userInfo: any) {
    return this.http.post(`${this.apiRoot}/api/Admin/DisableOrEnableUser`, userInfo, this.httpOptions).pipe(catchError(this.throwError<any>('disableEnableUser', 'userStatusMsg', 'User status was not updated.', null)));
  }

  public getAllNonSupervisors(): Observable<ServerModels.SupervisorsView[]> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      headers: headersObj
    };
    return this.http.get<ServerModels.SupervisorsView[]>(`${this.apiRoot}/api/Admin/GetAllNonSupervisors`, options).pipe(catchError(this.swallowError<any>('getAllNonSupervisors', 'defaultMsg', null, null)));
  }

  public getAllSupervisors(): Observable<ServerModels.SupervisorsView[]> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      headers: headersObj
    };
    return this.http.get<ServerModels.SupervisorsView[]>(`${this.apiRoot}/api/Admin/GetAllSupervisors`, options).pipe(catchError(this.swallowError<any>('getAllSupervisors', 'defaultMsg', null, null)));
  }

  public getAllUsers(query: string): Observable<ServerModels.UserInfoViewModel[]> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      headers: headersObj,
      params: {
        'query': query
      }
    };
    return this.http.get<ServerModels.UserInfoViewModel[]>(`${this.apiRoot}/api/Admin/GetAllUsers`, options).pipe(catchError(this.swallowError<any>('getAllUsers', 'defaultMsg', null, null)));
  }

  public getUserListPaged(e: { lazyLoadEvent: string }): Observable<{ usersList: ServerModels.UserInfoViewModel[], totalRecords: number }> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      headers: headersObj,
      params: e
    };
    this.loadingUsersList = true;
    return this.http.get<{ usersList: ServerModels.UserInfoViewModel[], totalRecords: number }>(`${this.apiRoot}/api/Admin/GetUserListPaged`, options).pipe(catchError(this.throwError<any>('getUserListPaged', 'defaultMsg', null, null)));;
  }

  public getUserListExport(e: { lazyLoadEvent: string }): Observable<ServerModels.UserInfoViewModel[]> {
    let options = {
      headers: this.httpOptions.headers,
      params: e
    };

    return this.http.get<ServerModels.UserInfoViewModel[]>(`${this.apiRoot}/api/Admin/GetUserListExport`, options).pipe(catchError(this.throwError<any>('getUserListExport', 'defaultMsg', null, null)));
  }

  public getDeniedApprovals(): Observable<ServerModels.ENESS_ApplicationUser[]> {
    return this.http.get<ServerModels.ENESS_ApplicationUser>(`${this.apiRoot}/api/Admin/GetDeniedApprovals`, this.httpOptions).pipe(catchError(this.throwError<any>('getDeniedApprovals', 'defaultMsg', null, null)));
  }

  public getImportedUsers(): Observable<ServerModels.ENESS_ImportedUsers[]> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      headers: headersObj
    };

    return this.http.get<ServerModels.ENESS_ImportedUsers>(`${this.apiRoot}/api/Admin/GetImportedUsersResult`, options).pipe(catchError(this.swallowError<any>('getImportedUsers', 'defaultMsg', null, null)));
  }

  public getPendingApprovals(): Observable<ServerModels.ENESS_ApplicationUser[]> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      headers: headersObj
    };
    return this.http.get<ServerModels.ENESS_ApplicationUser[]>(`${this.apiRoot}/api/Admin/GetPendingApprovals`, options).pipe(catchError(this.throwError<any>('getPendingApprovals', 'defaultMsg', null, null)));
  }

  public getPendingUserProfileChangeRequests(): Observable<ServerModels.ENESS_XREF_UserProfileChangeRequests[]> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      headers: headersObj
    };
    return this.http.get<ServerModels.ENESS_XREF_UserProfileChangeRequests[]>(`${this.apiRoot}/api/Account/GetPendingUserProfileChangeRequests`, options).pipe(catchError(this.throwError<any>('getPendingUserProfileChangeRequests', 'defaultMsg', null, null)));
  }

  public getUncertifiedUsers(): Observable<ServerModels.UserCertificationViewModel[]> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      headers: headersObj
    };
    return this.http.get<ServerModels.UserCertificationViewModel[]>(`${this.apiRoot}/api/Admin/GetUncertifiedUsers`, options).pipe(catchError(this.throwError<any>('getUncertifiedUsers', 'defaultMsg', null, null)));
  }

  public getCanUpdateUsers(): Observable<boolean> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      headers: headersObj
    };

    return this.http.get<boolean>(`${this.apiRoot}/api/Admin/CanUpdateUsers`, options).pipe(catchError(this.swallowError<any>('getCanUpdateUsers', 'defaultMsg', null, null))); 
  }

  public getUserHistoryByUsername(username: string): Observable<ServerModels.ENESS_UserHistory[]> {
    return this.http.get<ServerModels.ENESS_UserHistory[]>(`${this.apiRoot}/api/Admin/GetUserHistoryByUsername?username=${username}`, this.httpOptions).pipe(catchError(this.throwError<any>('getUserHistoryByUsername', 'defaultMsg', null, null)));
  }

  public updateUserByAdmin(userInfo: ServerModels.ENESS_XREF_UserProfileChangeRequests): Observable<ServerModels.UserInfoViewModel> {
    return this.http.post<ServerModels.UserInfoViewModel>(`${this.apiRoot}/api/Admin/UpdateUserProfile`, userInfo, this.httpOptions).pipe(catchError(this.throwError<any>('updateUserByAdmin', null, null, null)));
  }

  public userAccountCertification(userCertifications: ServerModels.ENESS_UserAccountCertification[]): Observable<ServerModels.UserCertificationViewModel[]> {
    return this.http.post<ServerModels.UserCertificationViewModel[]>(`${this.apiRoot}/api/Admin/UserAccountCertification`, userCertifications, this.httpOptions).pipe(catchError(this.swallowError<any>('userAccountCertification', 'defaultMsg', null, null))); 
  }

  public getAttachmentsGrid(): Observable<ServerModels.ENESS_ApplicationAttachment[]> {
    return this.http.get<ServerModels.ENESS_ApplicationAttachment[]>(`${this.apiRoot}/api/Attachments/GetAttachmentsGrid`, this.httpOptions).pipe(catchError(this.swallowError<any>('getAttachmentsGrid', 'defaultMsg', null, null))); 
  }

  public getAssignedFDLeadership(): Observable<ServerModels.ENESS_XREF_FieldDivisionLeadershipDTO[]> {
    return this.http.get<ServerModels.ENESS_XREF_FieldDivisionLeadershipDTO[]>(`${this.apiRoot}/api/Admin/GetAssignedFDLeadership`, this.httpOptions).pipe(catchError(this.throwError<any>('getAssignedFDLeadership', null, null, null)));
  }

  public getUnassignedFDLeadership(): Observable<ServerModels.ENESS_XREF_FieldDivisionLeadershipDTO[]> {
    return this.http.get<ServerModels.ENESS_XREF_FieldDivisionLeadershipDTO[]>(`${this.apiRoot}/api/Admin/GetUnassignedFDLeadership`, this.httpOptions).pipe(catchError(this.throwError<any>('getUnassignedFDLeadership', null, null, null)));
  }

  public assignFieldDivisionLeadership(leadershipList: ServerModels.ENESS_XREF_FieldDivisionLeadership[]): Observable<void> {
    return this.http.post<void>(`${this.apiRoot}/api/Admin/AssignFieldDivisionLeadership`, leadershipList, this.httpOptions).pipe(catchError(this.throwError<any>('assignFieldDivisionLeadership', null, null, null)));
  }

  public getRolesForUserById(userId: string): Observable<ServerModels.RoleViewModel[]> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'userId': userId
      }
    };
    return this.http.get<ServerModels.RoleViewModel[]>(`${this.apiRoot}/api/Admin/GetRolesForUserById`, options).pipe(catchError(this.throwError<any>('getRolesForUserById', 'defaultMsg', null, null)));
  }

  public getUserByAgencyId(agencyId: string): Observable<ServerModels.CDSUserViewModel[]> {
    return this.http.get(`${this.apiRoot}/api/Admin/GetUserByAgencyId?agencyId=${agencyId}`, this.httpOptions).pipe(catchError(this.throwError<any>('getUserByAgencyId', 'defaultMsg', null, null)));
  }

  public getAllCDSRequests(): Observable<ServerModels.CDSRequestViewModel[]> {
    return this.http.get(`${this.apiRoot}/api/Admin/GetAllCDSRequests`, this.httpOptions).pipe(catchError(this.throwError<any>('getAllCDSRequests', 'defaultMsg', null, null)));
  }

  public createCDSRequest(agencyId: string): Observable<ServerModels.CDSRequestViewModel> {
    return this.http.post(`${this.apiRoot}/api/Admin/CreateCDSRequest`, JSON.stringify(agencyId), this.httpOptions).pipe(catchError(this.throwError<any>('CreateCDSRequest', 'defaultMsg', null, null)));
  }

  public cancelCDSRequest(requestId: string) {
    return this.http.post(`${this.apiRoot}/api/Admin/CancelCDSRequest`, JSON.stringify(requestId), this.httpOptions).pipe(catchError(this.throwError<any>('cancelCDSRequest', 'defaultMsg', null, null)));
  }

  public denyCDSRequest (requestId: string) {
    return this.http.post(`${this.apiRoot}/api/Admin/DenyCDSRequest`, JSON.stringify(requestId), this.httpOptions).pipe(catchError(this.throwError<any>('DenyCDSRequest', 'defaultMsg', null, null)));
  }

  public approveCDSRequest(requestId: string) {
    return this.http.post(`${this.apiRoot}/api/Admin/ApproveCDSRequest`, JSON.stringify(requestId), this.httpOptions).pipe(catchError(this.throwError<any>('ApproveCDSRequest', 'defaultMsg', null, null)));
  }

  public stopCDSRequest(requestId: string) {
    return this.http.post(`${this.apiRoot}/api/Admin/StopCDSRequest`, JSON.stringify(requestId), this.httpOptions).pipe(catchError(this.throwError<any>('StopCDSRequest', 'defaultMsg', null, null)));
  }

    public loadUsersList(lazyLoadEvent: TableLazyLoadEvent) {
    //lazyLoadEvent.first = First row offset
    //lazyLoadEvent.rows = Number of rows per page
    //lazyLoadEvent.sortField = Field name to sort in single sort mode
    //lazyLoadEvent.sortOrder = Sort order as number, 1 for asc and -1 for dec in single sort mode
    //multiSortMeta: An array of SortMeta objects used in multiple columns sorting. Each SortMeta has field and order properties.
    //filters: Filters object having field as key and filter value, filter matchMode as value
    //globalFilter: Value of the global filter if available

    setTimeout(() => {
      this.loadingUsersList = true;
      this.getUserListPaged({ lazyLoadEvent: JSON.stringify(lazyLoadEvent) }).pipe(take(1)).subscribe((resp: { usersList: ServerModels.UserInfoViewModel[], totalRecords: number }) => {
        this.loadingUsersList = false;
        this.usersList$.next(resp.usersList);
        this.userListTotalRecords$.next(resp.totalRecords);
      },
        (err:any) => {
          this.loadingUsersList = false;
          this.usersList$.next([]);
        })
    }, 1000);
  }

  public elasticSearchReindexAll(params: string) {
    return this.http.get(`${this.apiRoot}/api/Admin/Elastic/Sync/all` + params).pipe(catchError(this.throwError<any>('elasticSearchReindexAll', 'defaultMsg', null, null)));
  }

  public elasticSearchReindexRecent(params: string) {
    return this.http.get(`${this.apiRoot}/api/Admin/Elastic/Sync/recent2` + params).pipe(catchError(this.throwError<any>('elasticSearchReindexRecent', 'defaultMsg', null, null)));
  }

  public elasticSearchReindexByCGID(params: string) {
    return this.http.get(`${this.apiRoot}/api/Admin/Elastic/Sync/CrimeGunByID` + params).pipe(catchError(this.throwError<any>('elasticSearchReindexByCGID', 'defaultMsg', null, null)));
  }

  public elasticSearchReindexByRMSID(params: string) {
    return this.http.get(`${this.apiRoot}/api/Admin/Elastic/Sync/CrimeGunByRMSID` + params).pipe(catchError(this.throwError<any>('elasticSearchReindexByRMSID', 'defaultMsg', null, null)));
  }

  public elasticSearchReindexByAgencyORI(params: string) {
    return this.http.get(`${this.apiRoot}/api/Admin/Elastic/Sync/CrimeGunByAgencyORI` + params).pipe(catchError(this.throwError<any>('elasticSearchReindexByAgencyORI', 'defaultMsg', null, null)));
  }

  public elasticSearchReindexBallisticEvidence(params: string) {
    return this.http.get(`${this.apiRoot}/api/Admin/Elastic/Sync/BallisticEvidence` + params).pipe(catchError(this.throwError<any>('elasticSearchReindexBallisticEvidence', 'defaultMsg', null, null)));
  }
  
}
